import { useEffect, useState } from 'react'

import useStorageReady from '../../hooks/useStorageReady'
import { useBoundStore } from '../../store/store'
import { isInNativeApp } from '../../utils/nativeBridge'

// Once-ever flag: set only when the user confirms they're 18+, so the gate is
// then suppressed for good on this device.
const STORAGE_KEY = 'age-verification-seen'
// The native bridge is normally injected before first paint, but can arrive a
// beat late; poll briefly so a logged-out native session still gets the gate.
export const BRIDGE_WAIT_CAP_MS = 1000
export const BRIDGE_POLL_MS = 100

// Storage can be denied / throw inside a WebView. Fail safe: if we can't read
// the flag, treat the user as not-yet-confirmed so the gate still shows.
const hasConfirmedAge = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null
  } catch {
    return false
  }
}

const AgeVerificationModal = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const username = useBoundStore(state => state.user.username)
  const theme = useBoundStore(state => state.common.theme)
  const logoDark = useBoundStore(state => state.settings.logo_dark.value)
  const logoLight = useBoundStore(state => state.settings.logo_light.value)
  // The persisted `user` slice can read empty for a beat on Safari's first
  // paint; wait for it so we don't flash the gate at a logged-in user.
  const storageReady = useStorageReady()

  const logoSrc = (theme === 'light' ? logoLight : logoDark) || '/dark.webp'

  useEffect(() => {
    if (!storageReady) return

    // A late credential restore (silent re-login) can flip `username` truthy
    // after the gate already opened — hide it so a now-logged-in member is not
    // left stuck behind a modal they should never see.
    if (username) {
      setIsVisible(false)
      return
    }

    if (hasConfirmedAge()) return

    // The wrapper usually injects its bridge before we mount, but poll a short
    // while in case it lands just after — otherwise a logged-out native session
    // whose bridge arrived late would never get the gate.
    let elapsed = 0
    let timer: ReturnType<typeof setTimeout>
    const check = () => {
      if (isInNativeApp()) {
        setIsVisible(true)
        return
      }
      if (elapsed >= BRIDGE_WAIT_CAP_MS) return
      elapsed += BRIDGE_POLL_MS
      timer = setTimeout(check, BRIDGE_POLL_MS)
    }
    check()

    return () => clearTimeout(timer)
  }, [storageReady, username])

  // "Sí" — confirm 18+: remember the choice and let the user through.
  const confirmAdult = () => {
    setIsVisible(false)
    try {
      localStorage.setItem(STORAGE_KEY, '1')
    } catch {
      // Safari private mode / quota — worst case the gate reappears next launch.
    }
  }

  // "No" — under-age: block access. Consent is intentionally NOT persisted, so
  // the gate returns on the next launch rather than granting a permanent pass.
  const blockAccess = () => setBlocked(true)

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-[400]">
      <div className="absolute inset-0 bg-black/60 animate-fadeIn" />
      <div className="absolute inset-0 flx-ctr px-6">
        <div className="relative w-full max-w-[400px] rounded-2xl px-6 pt-6 pb-7 bg-primary">
          <img
            className="w-[130px] aspect-[471_/_104] mx-auto"
            width={130}
            height={29}
            src={logoSrc}
            alt="Company Logo"
          />

          {blocked ? (
            <p className="text-txt text-base text-center mt-6">
              Lo sentimos, pero debes tener 18 años o más para acceder a este
              sitio. Gracias por tu comprensión.
            </p>
          ) : (
            <>
              <p className="text-txt text-xl font-bold text-center mt-5">
                Bienvenido a FUN88
              </p>
              <p className="text-txt text-base text-center mt-1.5">
                ¿Tienes 18 años o más?
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  className="flex-1 py-3 rounded-xl text-lg font-bold text-white bg-[#65676a]"
                  onClick={blockAccess}>
                  No
                </button>
                <button
                  className="flex-1 py-3 rounded-xl text-lg font-bold text-[#1a1a1a] green-metallic"
                  onClick={confirmAdult}>
                  Sí
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AgeVerificationModal
